import * as React from "react";
import { observer, inject } from "mobx-react";
import { toJS } from "mobx";
import * as moment from "moment";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPenSquare, faTrashAlt, faDownload, faPlus, faCheck } from "@fortawesome/free-solid-svg-icons";
import BootstrapTable from "react-bootstrap-table-next";
import { UncontrolledTooltip } from "reactstrap";

import { Invoice, InvoiceLineItem, InvoiceTaxLineItem, SettlementItem, SalesTax } from "src/model/Financial";
import { NumberToMonetaryStringFormat, CovertAndFormatCentsToDollars, FormatDollarString, ConvertToCents } from "src/common/Methods/FileUtil";
import { FormDialog } from "../common/FormDialog";
import { TextareaGroup, SelectGroup, InputGroup, DateGroup } from "../common/SimpleFormComponents";
import { successMessage, errorMessage } from "../common/Toast";
import { InvoiceRequest, InvoiceLineItemRequest } from "src/requests/FinancialRequests";
import { ConfigurationStore } from "src/stores/ConfigurationStore";
import InvoiceLineItemForm from "./InvoiceLineItemForm";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { CustomerStore } from "src/stores/CustomerStore";

const ARTIFICIAL_LINEITEM_COUNTER_START = 99999999999;

export namespace EditableInvoiceLineItem {
    export interface Fields {
        id: number;
        amount: string;
        entryTypeId: number | null;

        description: string;
        notes: string;
        entryDate: number | undefined;

        applicableTaxes: Array<SalesTax>;
    }

    export const Empty = (nextId: number): Fields => {
        return {
            id: nextId,
            amount: "",
            entryTypeId: null,

            description: "",
            notes: "",
            entryDate: undefined,

            applicableTaxes: []
        };
    };

    export const FromModel = (i: InvoiceLineItem): Fields => {
        return {
            id: i.id,
            amount: (i.amountInCents / 100).toString(),
            entryTypeId: i.entryType.id,

            applicableTaxes: i.applicableTaxes,

            description: i.description || "",
            notes: i.notes || "",
            entryDate: i.entryDate
        };
    };

    export const ToRequest = (f: Fields): InvoiceLineItemRequest => {
        if (f.entryTypeId === null) {
            throw new Error("Made it to EditableInvoiceLineItem.ToRequest with a null entry type");
        }
        return {
            id: f.id && f.id >= ARTIFICIAL_LINEITEM_COUNTER_START ? undefined : f.id,
            amountInCents: ConvertToCents(f.amount),
            entryTypeId: f.entryTypeId,

            description: f.description,
            notes: f.notes,
            entryDate: f.entryDate ? f.entryDate : new Date().getTime(),

            applicableTaxIds: f.applicableTaxes ? f.applicableTaxes.map(tax => tax.id) : []
        };
    };
}

export namespace EditableInvoice {
    export interface Fields {
        id: number | null;

        issueDate: number | null;
        dueDate: number | null;
        dateVoided: number | null;

        description: string;
        customerNotes: string;
        internalNotes: string;

        lineItems: Array<EditableInvoiceLineItem.Fields>;
        invoiceTaxes: Array<InvoiceTaxLineItem>;
        settlementItems: Array<SettlementItem>;

        balance: number | null;
    }

    export const Empty = (): Fields => {
        return {
            id: null,

            issueDate: new Date().getTime(),
            dueDate: null,
            dateVoided: null,

            description: "",
            customerNotes: "",
            internalNotes: "",

            lineItems: [],
            invoiceTaxes: [],
            settlementItems: [],

            balance: null
        };
    };

    export const FromModel = (i: Invoice) => {
        return {
            id: i.id,

            issueDate: i.issueDate,
            dueDate: i.dueDate,
            dateVoided: i.dateVoided,

            description: i.description || "",
            customerNotes: i.customerNotes || "",
            internalNotes: i.internalNotes || "",

            lineItems: i.lineItems.map(line => EditableInvoiceLineItem.FromModel(line)),
            invoiceTaxes: i.invoiceTaxes,
            settlementItems: i.settlementItems,

            balance: i.balance
        };
    };

    export const ToRequest = (i: Fields): InvoiceRequest => {
        if (i.dueDate === null) {
            throw new Error("In ToRequest Invoice with an unvalidated object");
        }
        return {
            dueDate: i.dueDate,
            customerNotes: i.customerNotes,
            internalNotes: i.internalNotes,
            description: i.description,
            lineItems: i.lineItems.map(lineItem => EditableInvoiceLineItem.ToRequest(lineItem))
        };
    };
}

export interface PageState {
    wasValidated: boolean;
    currentInvoiceLineItem: EditableInvoiceLineItem.Fields | null;
    artificialLineItemId: number;
    editedLineItemIds: Array<number>;
}

export interface Props {
    configurationStore?: ConfigurationStore;
    customerStore?: CustomerStore;

    currentEditingInvoice: Invoice | null;
    showInvoiceDialog: boolean;
    closeInvoiceDialog: () => void;
}

type State = PageState & EditableInvoice.Fields;

@inject("configurationStore")
@inject("customerStore")
@observer
export default class InvoicesDialog extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = this.defaultState();
    }

    defaultState = (): State => {
        return {
            wasValidated: false,
            currentInvoiceLineItem: null,
            artificialLineItemId: ARTIFICIAL_LINEITEM_COUNTER_START,
            editedLineItemIds: [],

            ...EditableInvoice.Empty()
        };
    };

    getValidDate = (date: any) => {
        if (this.state.issueDate) {
            return date.isSameOrAfter(moment(this.state.issueDate).subtract(1, "day"));
        } else {
            return date.isSameOrAfter(moment(new Date()).subtract(1, "day"));
        }
    };

    submit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.form && this.form.checkValidity() && this.state.lineItems.length > 0) {
            this.setState({ wasValidated: false });
            this.props.currentEditingInvoice ? this.editInvoice() : this.createInvoice();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    editInvoice = async () => {
        try {
            if (this.state.id) {
                const request = EditableInvoice.ToRequest(this.state);
                await this.props.customerStore!.editInvoice(this.state.id, request);
                successMessage(`Changes successfully saved.`);
                this.closeDialog();
            }
        } catch (error) {
            errorMessage(error);
        }
    };

    createInvoice = async () => {
        try {
            const request = EditableInvoice.ToRequest(this.state);
            await this.props.customerStore!.createInvoice(request);
            successMessage(`Invoice successfully created.`);
            this.closeDialog();
        } catch (error) {
            errorMessage(error);
        }
    };

    removeInvoiceLineItem = async (invoiceLineItemId: number) => {
        const goAhead = await SimpleConfirmation(`Are you sure you'd like to delete this line item from invoice #${this.state.id}?`);
        if (!goAhead) {
            return;
        }

        const newInvoiceLineItems = this.state.lineItems.filter(i => i.id !== invoiceLineItemId);
        this.setState({ lineItems: newInvoiceLineItems });
    };

    closeInlineItemForm = () => {
        this.setState({ currentInvoiceLineItem: null });
    };

    addOrEditInvoiceLineItems = (lineItem: EditableInvoiceLineItem.Fields) => {
        if (this.state.lineItems.find(l => l.id === lineItem.id)) {
            const arrayOldLineItems = this.state.lineItems.filter(l => l.id !== lineItem.id);
            this.setState({
                lineItems: [...arrayOldLineItems, lineItem],
                currentInvoiceLineItem: null,
                editedLineItemIds: this.isEditedLineItem(lineItem) ? [...this.state.editedLineItemIds, lineItem.id] : this.state.editedLineItemIds
            });
        } else {
            this.setState({
                lineItems: [...this.state.lineItems, lineItem],
                currentInvoiceLineItem: null,
                artificialLineItemId: this.state.artificialLineItemId + 1
            });
        }
    };

    isEditedLineItem = (lineItem: EditableInvoiceLineItem.Fields): boolean => {
        const currentInvoiceItem = this.state.currentInvoiceLineItem;
        if (!currentInvoiceItem) {
            throw new Error("In isEditedLineItem and currentInvoiceItem is null?");
        }
        const currentInvoiceTaxes = currentInvoiceItem.applicableTaxes.map(t => t.id).sort((a, b) => a - b);
        const newlineItemTaxes = lineItem.applicableTaxes.map(t => t.id).sort((a, b) => a - b);

        return currentInvoiceItem.entryTypeId === lineItem.entryTypeId &&
            currentInvoiceItem.amount === lineItem.amount &&
            currentInvoiceItem.description === lineItem.description &&
            JSON.stringify(currentInvoiceTaxes) === JSON.stringify(newlineItemTaxes)
            ? false
            : true;
    };

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.currentEditingInvoice !== null && prevProps.currentEditingInvoice !== this.props.currentEditingInvoice) {
            this.setState({
                ...EditableInvoice.FromModel(this.props.currentEditingInvoice)
            });
        } else if (this.props.currentEditingInvoice === null && prevProps.currentEditingInvoice !== null) {
            this.setState({ ...EditableInvoice.Empty() });
        }
    }

    closeDialog = () => {
        this.props.closeInvoiceDialog();
        this.setState(this.defaultState());
    };

    render() {
        const { configurationStore } = this.props;
        if (!configurationStore) {
            throw new Error(" ** No Configuration Store **");
        }

        const subtotal =
            this.state.lineItems.length > 0
                ? this.state.lineItems.reduce(
                      (amount, item: EditableInvoiceLineItem.Fields) =>
                          amount +
                          ConvertToCents(item.amount) * (configurationStore.findEntryDebitCreditById(item.entryTypeId) === "CREDIT" ? -1 : 1),
                      0
                  )
                : 0;

        const taxes = this.state.lineItems
            ? this.state.lineItems.reduce(
                  (amount, item: EditableInvoiceLineItem.Fields) =>
                      amount +
                      item.applicableTaxes.reduce(
                          (totalTax: number, tax: SalesTax) =>
                              totalTax +
                              ConvertToCents(item.amount) *
                                  tax.rate *
                                  (configurationStore.findEntryDebitCreditById(item.entryTypeId) === "CREDIT" ? -1 : 1),
                          0
                      ),
                  0
              )
            : 0;

        const total = taxes + subtotal;

        const lineItems = this.state.lineItems.sort((a, b) => a.id - b.id);

        const loading = this.props.customerStore!.loading;
        const disableForm: boolean =
            loading ||
            (this.props.currentEditingInvoice &&
                (this.props.currentEditingInvoice.dateVoided !== null || this.props.currentEditingInvoice.settlementItems.length > 0)) ||
            false;
        const lineItemsEmpty = this.state.lineItems.length === 0;

        const columnsInlineItems = [
            {
                dataField: "entryTypeId",
                text: "Type",
                formatter: (cellContent: any, row: EditableInvoiceLineItem.Fields) =>
                    configurationStore.entryTypes.filter(e => e.id === row.entryTypeId)[0].name
            },
            {
                dataField: "description",
                text: "Details",
                formatter: (cellContent: any, row: EditableInvoiceLineItem.Fields) => (row.description ? row.description : " - ")
            },
            {
                dataField: "amount",
                text: "Amount",
                formatter: (cellContent: any, row: EditableInvoiceLineItem.Fields) =>
                    row.amount
                        ? CovertAndFormatCentsToDollars(ConvertToCents(row.amount)) +
                          (configurationStore.findEntryDebitCreditById(row.entryTypeId) === "CREDIT" ? " CR" : "")
                        : ""
            },
            {
                dataField: "tax",
                text: "Taxes",
                formatter: (cellContent: any, row: EditableInvoiceLineItem.Fields) => {
                    if (row.applicableTaxes.length > 0) {
                        return row.applicableTaxes.map((tax, index: number) => tax.name + (row.applicableTaxes.length - 1 !== index ? ", " : ""));
                    } else {
                        return "-";
                    }
                }
            },
            {
                dataField: "actions",
                text: "Actions",
                headerStyle: { width: "80px" },
                formatExtraData: {
                    disableForm
                },
                formatter: (cellContent: any, row: EditableInvoiceLineItem.Fields, rowIndex: number, formatExtraData: { disableForm: boolean }) => {
                    return (
                        <>
                            <button
                                type="button"
                                id={`tooltipEdit${row.id}`}
                                className="btn btn-secondary btn-sm mr-1"
                                disabled={formatExtraData.disableForm}
                                onClick={() => this.setState({ currentInvoiceLineItem: row })}
                            >
                                <FontAwesomeIcon icon={faPenSquare} size="sm" />
                            </button>
                            <button
                                type="button"
                                id={`tooltipDelete${row.id}`}
                                className="btn btn-secondary btn-sm"
                                disabled={formatExtraData.disableForm}
                                onClick={() => this.removeInvoiceLineItem(row.id)}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} size="sm" />
                            </button>
                            <UncontrolledTooltip placement="top" target={`tooltipEdit${row.id}`} delay={0} className="tooltipstrap">
                                Edit Invoice line Item
                            </UncontrolledTooltip>
                            <UncontrolledTooltip placement="top" target={`tooltipDelete${row.id}`} delay={0} className="tooltipstrap">
                                Delete Invoice line Item
                            </UncontrolledTooltip>
                        </>
                    );
                }
            }
        ];

        return (
            <>
                <FormDialog
                    title={this.props.currentEditingInvoice ? `Edit Invoice #${this.state.id}` : `Create New Invoice`}
                    modalSize="modal-lg"
                    show={this.props.showInvoiceDialog}
                    onClose={() => this.closeDialog()}
                >
                    <>
                        <form
                            className={"px-2 needs-validation " + (this.state.wasValidated === true ? "was-validated" : "")}
                            noValidate={true}
                            onSubmit={this.submit}
                            ref={ref => (this.form = ref)}
                            id="invoiceForm"
                        >
                            <div className="form-row mb-3">
                                <div className="col-6">
                                    <label>Invoice Date:</label>
                                    <input
                                        type="text"
                                        className="form-control-plaintext"
                                        readOnly={true}
                                        value={this.state.issueDate ? moment(this.state.issueDate).format("YYYY/MM/DD") : ""}
                                    />
                                </div>
                                <DateGroup
                                    label="Due Date:"
                                    invalidFeedBack="Please provide a date."
                                    name="dueDate"
                                    className="col-sm-12 col-md-6"
                                    value={this.state.dueDate ? moment(this.state.dueDate).format("YYYY/MM/DD") : ""}
                                    disabled={disableForm}
                                    onChange={e => this.setState({ dueDate: e })}
                                    isRequired={true}
                                    icon={true}
                                    isValidDate={this.getValidDate}
                                />
                            </div>
                            <div className="form-row mb-4">
                                <TextareaGroup
                                    label="Invoice Description"
                                    rows={2}
                                    isRequired={false}
                                    name="notes"
                                    className="form-group col-md-12 col-lg-4"
                                    disabled={disableForm}
                                    value={this.state.description || ""}
                                    onChange={e => this.setState({ description: e.target.value })}
                                />
                                <TextareaGroup
                                    label="Customer Notes"
                                    rows={2}
                                    isRequired={false}
                                    name="customerNotes"
                                    className="form-group col-md-12 col-lg-4"
                                    disabled={disableForm}
                                    value={this.state.customerNotes || ""}
                                    onChange={e => this.setState({ customerNotes: e.target.value })}
                                />
                                <TextareaGroup
                                    label="Internal Notes"
                                    rows={2}
                                    isRequired={false}
                                    name="internalNotes"
                                    className="form-group col-md-12 col-lg-4"
                                    disabled={disableForm}
                                    value={this.state.internalNotes || ""}
                                    onChange={e => this.setState({ internalNotes: e.target.value })}
                                />
                            </div>
                        </form>
                        {this.state.currentInvoiceLineItem === null && lineItemsEmpty && (
                            <div className="form-row mb-2">
                                <div className="col-12">
                                    <div
                                        className={`alert alert-${this.state.wasValidated ? `danger` : `warning`} text-center mx-2`}
                                        style={this.state.wasValidated ? { border: "1px solid red" } : {}}
                                    >
                                        {" "}
                                        Click the button to add a line item to this invoice.
                                    </div>
                                </div>
                            </div>
                        )}
                        {this.state.currentInvoiceLineItem === null && (
                            <>
                                {/* <div className="form-row ">
                                <div className="col-12 px-3">
                                    <div className="alert alert-warning text-center" style={{fontSize: ".9rem"}}>
                                        The changes in line items only are applicable when the overall invoice had been saved.
                                    </div>
                                </div>
                            </div> */}
                                <div className="form-row  mb-2">
                                    <div className="col-12 px-3 text-right">
                                        <button
                                            className="btn btn-info btn-sm"
                                            disabled={disableForm}
                                            onClick={() =>
                                                this.setState({
                                                    currentInvoiceLineItem: EditableInvoiceLineItem.Empty(this.state.artificialLineItemId)
                                                })
                                            }
                                        >
                                            <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add line item
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        {this.state.currentInvoiceLineItem !== null && (
                            <InvoiceLineItemForm
                                invoiceLineItem={this.state.currentInvoiceLineItem}
                                closeInlineItemForm={() => this.closeInlineItemForm()}
                                addOrEditInvoiceLineItems={this.addOrEditInvoiceLineItems}
                                artificialLineItemId={this.state.artificialLineItemId}
                            />
                        )}
                        <div className="form-row">
                            <div className="col-12 px-3">
                                {/*<small className="text-info"> * Editing line items.</small>*/}
                                <BootstrapTable
                                    keyField="id"
                                    hover={true}
                                    bordered={false}
                                    striped={true}
                                    columns={columnsInlineItems}
                                    bootstrap4={true}
                                    classes="table table-sm mb-0"
                                    data={lineItems}
                                    rowClasses={(row: InvoiceLineItem, rowIndex: number): string => {
                                        let classes = "";
                                        if (this.state.editedLineItemIds.find(lineId => lineId === row.id)) {
                                            // if (this.state.currentInvoiceLineItem && this.state.currentInvoiceLineItem.id === row.id) {
                                            classes += "row-edited";
                                        }
                                        return classes;
                                    }}
                                    noDataIndication={() => (
                                        <div>
                                            <span>No results found.</span>
                                        </div>
                                    )}
                                />
                                <div className="col-12 py-0 pt-2 text-right d-flex" style={{ borderTop: "1px solid #ccc" }}>
                                    <div className="ml-auto">Subtotal: </div>
                                    <div className="text-right text-info" style={{ minWidth: "100px" }}>
                                        {CovertAndFormatCentsToDollars(subtotal)}
                                    </div>
                                </div>
                                <div className="col-12 py-0 text-right d-flex">
                                    <div className="ml-auto">Tax: </div>
                                    <div className="text-right text-info" style={{ minWidth: "100px" }}>
                                        {CovertAndFormatCentsToDollars(taxes)}
                                    </div>
                                </div>
                                <div className="col-12 py-0 text-right d-flex">
                                    <div className="ml-auto">Total: </div>
                                    <div className="text-right text-info" style={{ minWidth: "100px" }}>
                                        {CovertAndFormatCentsToDollars(total)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="form-row text-right mt-4">
                            <button type="button" className="btn btn-secondary mr-auto" onClick={() => this.closeDialog()}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                form="invoiceForm"
                                disabled={this.state.currentInvoiceLineItem !== null || disableForm}
                            >
                                {" "}
                                {this.props.currentEditingInvoice ? (
                                    <>
                                        <FontAwesomeIcon icon={faPenSquare} /> Edit
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCheck} /> Create
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                </FormDialog>
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/InvoiceDialog.tsx